/**
 * Marketplace Product Detail Page
 */
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  Chip,
  CircularProgress,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Image as ImageIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { PageHeader } from '../../components/common';
import { useGetMarketplaceProductQuery } from '../../store/api';
import { StockStatus } from '../../types/marketplace';
import { formatCurrency } from '../../utils/pricing';
import moment from 'moment';

const STOCK_STATUS_COLORS: Record<StockStatus, 'success' | 'error' | 'warning'> = {
  IN_STOCK: 'success',
  OUT_OF_STOCK: 'error',
  PREORDER: 'warning',
};

export const ProductDetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useGetMarketplaceProductQuery(Number(id));
  const product = data?.data;

  if (isLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !product) {
    return (
      <Box sx={{ p: 3, textAlign: 'center', py: 10 }}>
        <Typography color="error">{t('marketplaceCatalog.products.detail.notFound')}</Typography>
        <Button onClick={() => navigate('/marketplace/products')} sx={{ mt: 2 }}>
          {t('marketplaceCatalog.products.back')}
        </Button>
      </Box>
    );
  }

  const isBuyable = product.pricing_type === 'BUY_ONLY' || product.pricing_type === 'RENT_AND_BUY';
  const isRentable = product.pricing_type === 'RENT_ONLY' || product.pricing_type === 'RENT_AND_BUY';

  // A product flagged IN_STOCK but with no units on any path it actually sells
  // through is effectively out of stock — show what a customer would see.
  // Derived from the quantities rather than the server's `is_in_stock` so the
  // badge stays correct even against an API that doesn't send that field.
  const hasUnits =
    (isBuyable && product.sale_stock_quantity > 0) ||
    (isRentable && product.rental_stock_quantity > 0);
  const effectiveStockStatus: StockStatus =
    product.stock_status === 'IN_STOCK' && !hasUnits ? 'OUT_OF_STOCK' : product.stock_status;

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={product.name}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.products'), path: '/marketplace/products' },
          { label: product.name },
        ]}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/marketplace/products')}>
          {t('marketplaceCatalog.products.back')}
        </Button>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/marketplace/products/${product.id}/edit`)}
        >
          {t('marketplaceCatalog.products.detail.editProduct')}
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3 }}>
        {/* Main Content */}
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    {product.name}
                  </Typography>
                  {product.name_ar && (
                    <Typography variant="body1" dir="rtl" color="text.secondary">
                      {product.name_ar}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('marketplaceCatalog.products.detail.createdOn', { date: moment(product.created_at).format('MMMM DD, YYYY') })}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip label={product.is_active ? t('marketplaceCatalog.status.active') : t('marketplaceCatalog.status.inactive')} color={product.is_active ? 'success' : 'default'} />
                  {product.is_featured && <Chip label={t('marketplaceCatalog.form.featured')} color="warning" />}
                  <Chip label={t(`marketplaceCatalog.pricingType.${product.pricing_type}`)} color="info" />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">{t('marketplaceCatalog.cols.category')}</Typography>
                  <Typography variant="body1" fontWeight={500}>{product.category_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">{t('marketplaceCatalog.cols.subcategory')}</Typography>
                  <Typography variant="body1" fontWeight={500}>{product.subcategory_name}</Typography>
                </Box>
                <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                  <Typography variant="caption" color="text.secondary" display="block">{t('marketplaceCatalog.form.description')}</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{product.description || '—'}</Typography>
                </Box>
                {product.description_ar && (
                  <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                    <Typography variant="caption" color="text.secondary" display="block">{t('marketplaceCatalog.form.descriptionAr')}</Typography>
                    <Typography variant="body1" dir="rtl" sx={{ whiteSpace: 'pre-wrap' }}>{product.description_ar}</Typography>
                  </Box>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Pricing Summary */}
              <Typography variant="h6" fontWeight={600} gutterBottom>{t('marketplaceCatalog.products.sections.pricing')}</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                {isBuyable && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">{t('marketplaceCatalog.form.buyPrice')}</Typography>
                      <Typography variant="body1" fontWeight={600}>{formatCurrency(product.buy_price)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">{t('marketplaceCatalog.form.compareAtPrice')}</Typography>
                      <Typography variant="body1">{formatCurrency(product.compare_at_price)}</Typography>
                    </Box>
                  </>
                )}
                {isRentable && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">{t('marketplaceCatalog.form.dailyRentalPrice')}</Typography>
                      <Typography variant="body1" fontWeight={600}>{formatCurrency(product.daily_rental_price)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">{t('marketplaceCatalog.form.weeklyRentalPrice')}</Typography>
                      <Typography variant="body1">{formatCurrency(product.weekly_rental_price)}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">{t('marketplaceCatalog.form.minRentalDays')}</Typography>
                      <Typography variant="body1">{product.min_rental_days}</Typography>
                    </Box>
                  </>
                )}
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Insurance */}
              <Typography variant="h6" fontWeight={600} gutterBottom>{t('marketplaceCatalog.products.sections.insurance')}</Typography>
              {product.insurance_available ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">{t('marketplaceCatalog.form.insurancePrice')}</Typography>
                    <Typography variant="body1" fontWeight={600}>{formatCurrency(product.insurance_price)}</Typography>
                  </Box>
                  <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                    <Typography variant="caption" color="text.secondary" display="block">{t('marketplaceCatalog.form.description')}</Typography>
                    <Typography variant="body1">{product.insurance_description || '—'}</Typography>
                  </Box>
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">{t('marketplaceCatalog.products.detail.insuranceNotAvailable')}</Typography>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Warranty */}
              <Typography variant="h6" fontWeight={600} gutterBottom>{t('marketplaceCatalog.form.warranty')}</Typography>
              <Typography variant="body1">{product.warranty || '—'}</Typography>
              {product.warranty_ar && (
                <Typography variant="body1" dir="rtl" color="text.secondary">{product.warranty_ar}</Typography>
              )}

              {/* Specifications */}
              {product.specifications && product.specifications.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" fontWeight={600} gutterBottom>{t('marketplaceCatalog.products.sections.specifications')}</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('marketplaceCatalog.form.name')}</TableCell>
                          <TableCell>{t('marketplaceCatalog.form.value')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {product.specifications.map((spec, index) => (
                          <TableRow key={spec.id ?? index}>
                            <TableCell>
                              {spec.name}
                              {spec.name_ar && (
                                <Typography variant="caption" color="text.secondary" display="block" dir="rtl">
                                  {spec.name_ar}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {spec.value}
                              {spec.value_ar && (
                                <Typography variant="caption" color="text.secondary" display="block" dir="rtl">
                                  {spec.value_ar}
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Sidebar */}
        <Box>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>{t('marketplaceCatalog.products.mainImage')}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                <Avatar src={product.main_image || undefined} variant="rounded" sx={{ width: 280, height: 280, maxWidth: '100%' }}>
                  <ImageIcon sx={{ fontSize: 120 }} />
                </Avatar>
              </Box>
              {product.images && product.images.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>{t('marketplaceCatalog.products.detail.gallery')}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {product.images.map((img) => (
                      <Avatar key={img.id} src={img.image} variant="rounded" sx={{ width: 64, height: 64 }}>
                        <ImageIcon />
                      </Avatar>
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>{t('marketplaceCatalog.products.detail.overview')}</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('marketplaceCatalog.form.stockStatus')}</Typography>
                  <Chip
                    label={t(`marketplaceCatalog.stockStatus.${effectiveStockStatus}`)}
                    size="small"
                    color={STOCK_STATUS_COLORS[effectiveStockStatus]}
                  />
                </Box>
                {(product.pricing_type === 'BUY_ONLY' || product.pricing_type === 'RENT_AND_BUY') && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('marketplaceCatalog.form.saleStockQuantity')}</Typography>
                    <Typography variant="body2" fontWeight={600}>{product.sale_stock_quantity}</Typography>
                  </Box>
                )}
                {(product.pricing_type === 'RENT_ONLY' || product.pricing_type === 'RENT_AND_BUY') && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">{t('marketplaceCatalog.form.rentalStockQuantity')}</Typography>
                    <Typography variant="body2" fontWeight={600}>{product.rental_stock_quantity}</Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">{t('marketplaceCatalog.cols.rating')}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <StarIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                    <Typography variant="body2" fontWeight={600}>
                      {parseFloat(product.rating_average || '0').toFixed(1)} ({product.reviews_count})
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">{t('marketplaceCatalog.form.order')}</Typography>
                  <Typography variant="body2" fontWeight={600}>{product.order}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductDetailPage;
